import { useState, useEffect } from 'react';
import { ConfigProvider, Form, message, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { smsService, authService, userService } from '@/services/amai';
import { setToken, setUserInfo, clearAllUserData, getInviteCode, isHandler } from '@/utils/auth';
import { parseExceptionError } from '@/utils/errorHandler';
import { eventBus } from '@/utils/eventBus';
import { resolveBrand } from '@/config/brand';
import {
  PhonePrefix,
  PhoneDivider,
  PhoneInput,
  VerificationInput,
  SendCodeButton,
  AgreementLink,
  SubmitButton,
  ArrowIcon,
  Ttilt,
} from '@/pages/login/styles';
import {
  LoginModalContainer,
  CloseButton,
  ModalTitle,
  ModalLoginForm,
  ModalPhoneInputContainer,
  ModalVerificationContainer,
  ModalAgreementText,
} from './styles';
import Welcome from './welcome';

interface LoginFormData {
  phone: string;
  code: string;
  countryCode: string;
}

export default function LoginModal() {
  const { t } = useTranslation();
  const [form] = Form.useForm<LoginFormData>();
  const [visible, setVisible] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  

  // 监听 showLoginModal 事件（H5路由下不响应，由H5自己的Login组件处理）
  useEffect(() => {
    const handler = () => {
      const path = window.location.pathname;
      if (path.startsWith('/h5/')) return;
      setVisible(true);
    };
    eventBus.on('showLoginModal', handler);
    return () => eventBus.off('showLoginModal', handler);
  }, []);

  // 关闭弹框并重置所有状态
  const handleClose = async () => {
    setShowWelcome(false);
    setVisible(false);
      // try{
      //     await userService.getNewcomerPack()
      //   }catch(error){
      //     message.error('领取失败')
      //   }
  };

  // 监听表单字段变化
  const handleFormValuesChange = (_changedValues: any, allValues: LoginFormData) => {
    if (allValues.phone) {
      const phoneRegex = /^1[3-9]\d{9}$/;
      setIsPhoneValid(phoneRegex.test(allValues.phone));
    } else {
      setIsPhoneValid(false);
    }

    const phoneRegex = /^1[3-9]\d{9}$/;
    const isPhoneFormatValid = allValues.phone && phoneRegex.test(allValues.phone);
    const isCodeValid = allValues.code && /^\d{6}$/.test(allValues.code);
    setIsSubmitDisabled(!(isPhoneFormatValid && isCodeValid));
  };

  // 倒计时效果
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 键盘事件处理
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        const activeElement = document.activeElement;
        if (activeElement?.getAttribute('placeholder') === t('login.codePlaceholder')) {
          form.submit();
        }
      }
    };
    document.addEventListener('keypress', handleKeyPress);
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [form]);

  // 发送验证码
  const handleSendCode = async () => {
    try {
      const phone = form.getFieldValue('phone');
      const countryCode = form.getFieldValue('countryCode') || '+86';

      if (!phone) {
        message.error(t('login.phoneRequired'));
        return;
      }

      if (countryCode === '+86') {
        const phoneRegex = /^1[3-9]\d{9}$/;
          if (!phoneRegex.test(phone)) {
          message.error(t('login.phoneInvalid'));
          return;
        }
      } else {
        const internationalPhoneRegex = /^\d{6,}$/;
        if (!internationalPhoneRegex.test(phone)) {
          message.error(t('login.phoneInvalid'));
          return;
        }
      }

      setSendingCode(true);
      const response = await smsService.sendLoginCode(phone, countryCode.replace('+', ''));

      if (response.code === 0) {
        message.success(t('login.sendCodeSuccess'));
        setCountdown(60);
      } else {
        message.error(response.message || t('login.sendCodeFailed'));
      }
    } catch (error: any) {
      const errorInfo = parseExceptionError(error);
      message.error(errorInfo.message);
    } finally {
      setSendingCode(false);
    }
  };

  // 登录提交
  const handleSubmit = async (values: LoginFormData) => {
    try {
      setLoading(true);
      const response = await authService.smsLogin({
        mobile: values.phone,
        country_code: '86',
        sms_code: values.code,
        source: getInviteCode() ? 1 : 2
      });

      if (response.code === 0 && response.data) {
        try {
          await clearAllUserData();
        } catch (clearError) {
          console.warn('[LoginModal] 清理之前的用户数据失败:', clearError);
        }

        const { token, user_id, user_name, user_avatar, mobile } = response.data;
        setToken(token);

        // 通知 Sidebar 更新用户信息
        eventBus.emit('userInfoUpdated', {
          user_id,
          user_name,
          user_avatar,
          mobile,
        });

        message.success(t('login.loginSuccess'));

        // 独立运营站点（如 gxzs 高新智算）：登录成功后整页刷新，随新 token 重新拉取页面数据与认证态
        if (resolveBrand().useLoginModal) {
          window.location.reload();
          return;
        }

        let showNewUserGift;
        try {
          const profileRes = await authService.getUserProfile(true);
          if (profileRes.code === 0 && profileRes.data) {
            showNewUserGift = profileRes.data.show_new_user_gift;
            setUserInfo({
              user_id,
              id: user_id,
              username: user_name || '',
              nickname: user_name || '',
              avatar: user_avatar,
              mobile,
              role: 'user',
              // 是否展示新人礼包
              show_new_user_gift: showNewUserGift,
            });
          }
        } catch (profileError) {
          console.warn('[LoginModal] 获取用户信息失败:', profileError);
        }
        setVisible(false);
        setShowWelcome(showNewUserGift ?? false);
      } else {
        message.error((response as any).msg || t('login.loginFailed'));
      }
    } catch (error: any) {
      const errorInfo = parseExceptionError(error);
      message.error(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };



  if (showWelcome) return <Welcome onClose={handleClose} />;

  if (!visible) return null;

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorBgBase: '#121212',
          colorBgContainer: '#121212',
          borderRadius: 4,
          colorPrimary: '#FF6A39',
          colorLink: '#dc5d33',
        },
      }}
    >
      <LoginModalContainer>
        {/* 关闭按钮 */}
        <CloseButton onClick={handleClose}>✕</CloseButton>

        <ModalTitle isWelcome={!isHandler()}>{!isHandler()?t('login.welcomeTitle'):t('login.handlerTitle')}</ModalTitle>
        <Ttilt>{!isHandler()?t('login.subtitle'):''}</Ttilt>
        {/* <SectionTitle>手机号登录</SectionTitle> */}

        <ModalLoginForm
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ countryCode: '+86' }}
          onValuesChange={handleFormValuesChange}
        >
          <Form.Item
            name="phone"
            rules={[
              { required: true, message: t('login.phoneRequired') },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const phoneRegex = /^1[3-9]\d{9}$/;
                  if (!phoneRegex.test(value)) {
                    return Promise.reject(new Error(t('login.phoneInvalid')));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <ModalPhoneInputContainer>
              <PhonePrefix>+86</PhonePrefix>
              <PhoneDivider />
              <PhoneInput placeholder={t('login.phonePlaceholder')} />
            </ModalPhoneInputContainer>
          </Form.Item>

          <Form.Item
            name="code"
            rules={[
              { required: true, message: t('login.codeRequired') },
              { pattern: /^\d{6}$/, message: t('login.codeInvalid') },
            ]}
          >
            <ModalVerificationContainer>
              <VerificationInput placeholder={t('login.codePlaceholder')} />
              <SendCodeButton
                type="primary"
                className={countdown > 0 ? 'countdown' : ''}
                onClick={handleSendCode}
                disabled={!isPhoneValid || sendingCode || countdown > 0}
              >
                {countdown > 0 ? `${countdown} s` : t('login.sendCode')}
              </SendCodeButton>
            </ModalVerificationContainer>
          </Form.Item>

          <ModalAgreementText>
            {t('login.agreementPrefix')}{' '}
            <AgreementLink href="/agreement/privacy-policy" target="_blank">
              {t('login.agreementLinkText')}
            </AgreementLink>
          </ModalAgreementText>

          <Form.Item>
            <SubmitButton
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              disabled={isSubmitDisabled || loading}
            >
              {t('login.submitBtn')}
              <ArrowIcon>
                {/* <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="13"
                  viewBox="0 0 14 13"
                  fill="none"
                >
                  <path
                    d="M13.9302 6.26784C13.9022 6.10238 13.8112 5.94329 13.6712 5.83511L8.33719 0.986021C8.01519 0.737839 7.53219 0.769657 7.25219 1.06238C7.00719 1.32329 7.00719 1.71148 7.25219 1.97238L11.3192 5.66966H0.875192C0.448192 5.66966 0.105192 5.98148 0.105192 6.36966C0.105192 6.75784 0.448192 7.06966 0.875192 7.06966H11.3192L7.25219 10.7669C6.95119 11.0406 6.95119 11.4797 7.24519 11.7533C7.54619 12.0269 8.02919 12.0269 8.33019 11.7597L13.7202 6.86602C13.8882 6.70693 13.9652 6.4842 13.9302 6.26784Z"
                    fill="white"
                  />
                </svg> */}
              </ArrowIcon>
            </SubmitButton>
          </Form.Item>
        </ModalLoginForm>
      </LoginModalContainer>
    </ConfigProvider>
  );
}
